<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import draggable from 'vuedraggable'

// Components
import TextBlock from './components/blocks/TextBlock.vue'
import DataBlock from './components/blocks/DataBlock.vue'
import TableBlock from './components/blocks/TableBlock.vue'
import ImageBlock from './components/blocks/ImageBlock.vue'
import TextEdit from './components/editors/TextEdit.vue'
import DataEdit from './components/editors/DataEdit.vue'
import TableEdit from './components/editors/TableEdit.vue'
import ImageEdit from './components/editors/ImageEdit.vue'

// Interfaces
interface Block {
  id: string
  type: 'text' | 'data' | 'table' | 'image'
  data: any
  style: any
}

interface Page {
  id: string
  header?: {
    title: string
    subtitle: string
  }
  footer?: {
    content: string
  }
  blocks: Block[]
}

interface Report {
  title: string
  pages: Page[]
  formData: any
}

// Props - dados do formulário que já existem
const props = defineProps<{
  formData: any
}>()

// Estado
const report = reactive<Report>({
  title: 'Relatório de Higiene Ocupacional',
  pages: [],
  formData: {}
})

const selectedBlock = ref<Block | null>(null)

// Inicialização com dados existentes
onMounted(() => {
  initializeReport()
})

function initializeReport() {
  report.formData = props.formData
  report.pages = [createNewPage()]
  
  // Adiciona blocos iniciais com os dados do formulário
  const firstPage = report.pages[0]
  
  // Bloco de título
  firstPage.blocks.push(createTextBlock('<h2>Relatório de Higiene Ocupacional</h2>'))
  
  // Bloco de dados gerais
  firstPage.blocks.push(createDataBlock('dados-gerais', 'Dados Gerais da Avaliação'))
  
  // Bloco de tabela com parâmetros
  firstPage.blocks.push(createTableBlock('parametros', 'Parâmetros Avaliados'))
  
  // Bloco de observações
  firstPage.blocks.push(createTextBlock('<h3>Observações e Recomendações</h3><p>Digite as observações aqui...</p>'))
}

function createNewPage(): Page {
  return {
    id: generateId(),
    header: {
      title: 'Relatório de Higiene Ocupacional',
      subtitle: 'Sistema de Gestão de Higiene Ocupacional'
    },
    footer: {
      content: `Página 1 | Emitido em ${new Date().toLocaleDateString('pt-BR')}`
    },
    blocks: []
  }
}

function createTextBlock(content: string): Block {
  return {
    id: generateId(),
    type: 'text',
    data: { content },
    style: { fontSize: '14px', lineHeight: '1.6' }
  }
}

function createDataBlock(dataKey: string, title: string): Block {
  return {
    id: generateId(),
    type: 'data',
    data: { 
      dataKey,
      title,
      fields: getDataFields(dataKey)
    },
    style: { backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px' }
  }
}

function createTableBlock(dataKey: string, title: string): Block {
  return {
    id: generateId(),
    type: 'table',
    data: {
      dataKey,
      title,
      columns: getTableColumns(dataKey),
      data: getTableData(dataKey)
    },
    style: { margin: '20px 0' }
  }
}

function createImageBlock(): Block {
  return {
    id: generateId(),
    type: 'image',
    data: { src: '', alt: 'Imagem do relatório', caption: '' },
    style: { textAlign: 'center', margin: '20px 0' }
  }
}

// Funções auxiliares para obter dados do formulário
function getDataFields(dataKey: string) {
  const fieldsMap: any = {
    'dados-gerais': [
      { label: 'Local', value: report.formData?.local || '' },
      { label: 'Data da Avaliação', value: report.formData?.assessmentDate || '' },
      { label: 'Avaliador', value: report.formData?.evaluator || '' },
      { label: 'Empresa', value: report.formData?.company || '' }
    ]
  }
  return fieldsMap[dataKey] || []
}

function getTableColumns(dataKey: string) {
  const columnsMap: any = {
    'parametros': [
      { key: 'parameter', label: 'Parâmetro', editable: true },
      { key: 'value', label: 'Valor Medido', editable: true },
      { key: 'limit', label: 'Limite', editable: true },
      { key: 'unit', label: 'Unidade', editable: true },
      { key: 'status', label: 'Status', editable: false }
    ]
  }
  return columnsMap[dataKey] || []
}

function getTableData(dataKey: string) {
  const dataMap: any = {
    'parametros': report.formData?.parameters?.map((param: any) => ({
      parameter: param.name,
      value: param.measuredValue,
      limit: param.limit,
      unit: param.unit,
      status: param.measuredValue <= param.limit ? 'Dentro' : 'Acima'
    })) || []
  }
  return dataMap[dataKey] || []
}

// Métodos do Editor
function addPage() {
  report.pages.push(createNewPage())
}

function removePage(pageIndex: number) {
  if (report.pages.length > 1) {
    report.pages.splice(pageIndex, 1)
  }
}

function movePageUp(pageIndex: number) {
  if (pageIndex > 0) {
    const page = report.pages.splice(pageIndex, 1)[0]
    report.pages.splice(pageIndex - 1, 0, page)
  }
}

function movePageDown(pageIndex: number) {
  if (pageIndex < report.pages.length - 1) {
    const page = report.pages.splice(pageIndex, 1)[0]
    report.pages.splice(pageIndex + 1, 0, page)
  }
}

function addTextBlock() {
  const currentPage = report.pages[report.pages.length - 1]
  currentPage.blocks.push(createTextBlock('<p>Digite seu texto aqui...</p>'))
}

function addDataBlock() {
  const currentPage = report.pages[report.pages.length - 1]
  currentPage.blocks.push(createDataBlock('custom-data', 'Dados Personalizados'))
}

function addTableBlock() {
  const currentPage = report.pages[report.pages.length - 1]
  currentPage.blocks.push(createTableBlock('custom-table', 'Tabela Personalizada'))
}

function addImageBlock() {
  const currentPage = report.pages[report.pages.length - 1]
  currentPage.blocks.push(createImageBlock())
}

function removeBlock(pageIndex: number, blockIndex: number) {
  report.pages[pageIndex].blocks.splice(blockIndex, 1)
  selectedBlock.value = null
}

function moveBlockUp(pageIndex: number, blockIndex: number) {
  if (blockIndex > 0) {
    const blocks = report.pages[pageIndex].blocks
    const block = blocks.splice(blockIndex, 1)[0]
    blocks.splice(blockIndex - 1, 0, block)
  }
}

function moveBlockDown(pageIndex: number, blockIndex: number) {
  const blocks = report.pages[pageIndex].blocks
  if (blockIndex < blocks.length - 1) {
    const block = blocks.splice(blockIndex, 1)[0]
    blocks.splice(blockIndex + 1, 0, block)
  }
}

function selectBlock(block: Block) {
  selectedBlock.value = block
}

function updateBlockData(pageIndex: number, blockIndex: number, newData: any) {
  report.pages[pageIndex].blocks[blockIndex].data = {
    ...report.pages[pageIndex].blocks[blockIndex].data,
    ...newData
  }
}

function onBlockEdit(updatedBlock: Block) {
  if (selectedBlock.value) {
    Object.assign(selectedBlock.value, updatedBlock)
  }
}

function onContentEdit(event: Event) {
  // Salva automaticamente as alterações de conteúdo
  saveReport()
}

function onBlockReorder() {
  saveReport()
}

function updateHeader(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement
  if (report.pages[pageIndex].header) {
    report.pages[pageIndex].header!.title = target.innerText
  }
}

function updateSubtitle(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement
  if (report.pages[pageIndex].header) {
    report.pages[pageIndex].header!.subtitle = target.innerText
  }
}

function updateFooter(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement
  if (report.pages[pageIndex].footer) {
    report.pages[pageIndex].footer!.content = target.innerText
  }
}

// Componentes dinâmicos
function getBlockComponent(type: string) {
  const components: any = {
    text: TextBlock,
    data: DataBlock,
    table: TableBlock,
    image: ImageBlock
  }
  return components[type]
}

function getEditComponent(type: string) {
  const components: any = {
    text: TextEdit,
    data: DataEdit,
    table: TableEdit,
    image: ImageEdit
  }
  return components[type]
}

// Utilitários
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

function saveReport() {
  // Salvar no localStorage ou API
  localStorage.setItem('occupational-hygiene-report', JSON.stringify(report))
  console.log('Relatório salvo:', report)
}

function exportPDF() {
  // Implementar exportação PDF
  alert('Exportando para PDF...')
}
</script>

<template>
  <div class="report-editor">
    <!-- Barra de Ferramentas -->
    <div class="toolbar">
      <div class="toolbar-group">
        <button @click="addTextBlock" class="toolbar-btn">
          <span>📝</span> Texto
        </button>
        <button @click="addDataBlock" class="toolbar-btn">
          <span>📊</span> Dados
        </button>
        <button @click="addTableBlock" class="toolbar-btn">
          <span>📋</span> Tabela
        </button>
        <button @click="addImageBlock" class="toolbar-btn">
          <span>🖼️</span> Imagem
        </button>
      </div>
      
      <div class="toolbar-group">
        <button @click="addPage" class="toolbar-btn">
          <span>📄</span> Nova Página
        </button>
        <button @click="exportPDF" class="toolbar-btn primary">
          <span>📤</span> Exportar PDF
        </button>
        <button @click="saveReport" class="toolbar-btn secondary">
          <span>💾</span> Salvar
        </button>
      </div>
    </div>

    <!-- Páginas do Relatório -->
    <div class="pages-container">
      <div 
        v-for="(page, pageIndex) in report.pages" 
        :key="page.id"
        class="page-wrapper"
      >
        <div class="page-controls">
          <button @click="removePage(pageIndex)" class="page-btn danger">
            🗑️
          </button>
          <span class="page-number">Página {{ pageIndex + 1 }}</span>
          <button @click="movePageUp(pageIndex)" class="page-btn" :disabled="pageIndex === 0">
            ⬆️
          </button>
          <button @click="movePageDown(pageIndex)" class="page-btn" :disabled="pageIndex === report.pages.length - 1">
            ⬇️
          </button>
        </div>

        <div class="a4-page">
          <!-- Cabeçalho -->
          <div class="page-header" v-if="page.header">
            <div class="header-content">
              <h1 
                contenteditable="true" 
                @blur="updateHeader(pageIndex, $event)"
                @input="onContentEdit"
              >
                {{ page.header.title }}
              </h1>
              <div 
                contenteditable="true" 
                class="header-subtitle"
                @blur="updateSubtitle(pageIndex, $event)"
                @input="onContentEdit"
              >
                {{ page.header.subtitle }}
              </div>
            </div>
          </div>

          <!-- Conteúdo da Página -->
          <div class="page-content">
            <draggable 
              v-model="page.blocks"
              item-key="id"
              class="blocks-container"
              @end="onBlockReorder"
              handle=".drag-handle"
            >
              <template #item="{ element: block, index: blockIndex }">
                <div 
                  :class="['block', `block-${block.type}`, { 'selected': selectedBlock?.id === block.id }]"
                  @click="selectBlock(block)"
                >
                  <!-- Controles do Bloco -->
                  <div class="block-controls">
                    <div class="drag-handle">⠿</div>
                    <button @click="removeBlock(pageIndex, blockIndex)" class="block-btn danger">
                      ×
                    </button>
                    <button @click="moveBlockUp(pageIndex, blockIndex)" class="block-btn" :disabled="blockIndex === 0">
                      ↑
                    </button>
                    <button @click="moveBlockDown(pageIndex, blockIndex)" class="block-btn" :disabled="blockIndex === page.blocks.length - 1">
                      ↓
                    </button>
                  </div>

                  <!-- Renderização do Bloco -->
                  <component
                    :is="getBlockComponent(block.type)"
                    :block="block"
                    :pageIndex="pageIndex"
                    :blockIndex="blockIndex"
                    @update="updateBlockData(pageIndex, blockIndex, $event)"
                    @content-edit="onContentEdit"
                  />
                </div>
              </template>
            </draggable>
          </div>

          <!-- Rodapé -->
          <div class="page-footer" v-if="page.footer">
            <div 
              contenteditable="true" 
              class="footer-content"
              @blur="updateFooter(pageIndex, $event)"
              @input="onContentEdit"
            >
              {{ page.footer.content }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Painel de Edição -->
    <div v-if="selectedBlock" class="edit-panel">
      <div class="edit-panel-header">
        <h3>Editar Bloco</h3>
        <button @click="selectedBlock = null" class="close-btn">×</button>
      </div>
      
      <div class="edit-panel-content">
        <component
          :is="getEditComponent(selectedBlock.type)"
          :block="selectedBlock"
          @update="onBlockEdit"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.report-editor {
  min-height: 100vh;
  background: #f0f2f5;
  padding: 20px;
}

.toolbar {
  background: white;
  padding: 15px 20px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .toolbar-group {
    display: flex;
    gap: 10px;
  }
}

.toolbar-btn {
  padding: 10px 15px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #007bff;
  }
  
  &.primary {
    background: #007bff;
    color: white;
    border-color: #007bff;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.secondary {
    background: #28a745;
    color: white;
    border-color: #28a745;
    
    &:hover {
      background: #1e7e34;
    }
  }
}

.pages-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
}

.page-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding: 8px 15px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.1);
}

.page-btn {
  padding: 5px 8px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background: #f8f9fa;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &.danger:hover {
    background: #dc3545;
    color: white;
  }
}

.page-number {
  font-weight: bold;
  margin: 0 10px;
}

.a4-page {
  width: 794px;
  min-height: 1123px;
  background: white;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  position: relative;
}

.page-header {
  padding: 30px 40px 20px;
  border-bottom: 2px solid #e9ecef;
  text-align: center;
  
  h1 {
    margin: 0;
    font-size: 24px;
    color: #333;
    
    &:focus {
      outline: 2px dashed #007bff;
      outline-offset: 2px;
    }
  }
  
  .header-subtitle {
    margin-top: 10px;
    color: #666;
    font-size: 14px;
    
    &:focus {
      outline: 2px dashed #007bff;
      outline-offset: 2px;
    }
  }
}

.page-content {
  flex: 1;
  padding: 20px 40px;
}

.blocks-container {
  min-height: 800px;
}

.block {
  position: relative;
  margin: 15px 0;
  border: 2px solid transparent;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #dee2e6;
  }
  
  &.selected {
    border-color: #007bff;
    background: #f8f9ff;
  }
}

.block-controls {
  position: absolute;
  top: -30px;
  right: 0;
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  .block:hover & {
    opacity: 1;
  }
}

.drag-handle {
  padding: 2px 6px;
  background: #6c757d;
  color: white;
  border-radius: 4px 0 0 4px;
  cursor: grab;
  font-size: 12px;
}

.block-btn {
  padding: 2px 6px;
  background: #6c757d;
  color: white;
  border: none;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: #545b62;
  }
  
  &.danger:hover {
    background: #dc3545;
  }
}

.page-footer {
  padding: 20px 40px;
  border-top: 1px solid #e9ecef;
  text-align: center;
  color: #666;
  font-size: 12px;
  
  .footer-content:focus {
    outline: 2px dashed #007bff;
    outline-offset: 2px;
  }
}

.edit-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100vh;
  background: white;
  box-shadow: -2px 0 10px rgba(0,0,0,0.1);
  border-left: 1px solid #dee2e6;
}

.edit-panel-header {
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    margin: 0;
    font-size: 16px;
  }
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
}

.edit-panel-content {
  padding: 20px;
}

@media print {
  .toolbar,
  .page-controls,
  .block-controls,
  .edit-panel {
    display: none !important;
  }
  
  .a4-page {
    box-shadow: none;
    margin: 0;
    page-break-after: always;
  }
  
  .block {
    border: none !important;
  }
}
</style>